import { Shot, ShotData } from '../types/shots';
import {
	EditShot,
	LoadShots,
	BulkEditShots,
	DeleteShot,
	LoadShot,
	GenerateShotThumbnail,
	GenerateAllShotThumbnails,
} from '../../connection/shots';
import { AppSettings } from '../types/settings';
import { Entity } from '../../types/interfaces';

interface AsyncAction {
	type: string;
	promise: Promise<any>
}

interface Action {
	type: string;
	payload: any;
}

export const ADD_SHOT = 'ADD_SHOT';
export function addShot(shot: Shot): Action {
	return {
		type: ADD_SHOT,
		payload: shot,
	};
}

export const EDIT_SHOT = 'EDIT_SHOT';
export function editShot(
	token: string,
	shot: Shot,
	meetingSlug?: string,
	channelId?: number,
	useNewShotThumbs: boolean = false,
	skipThumbnailRegen: boolean = false,
): AsyncAction {
	return {
		type: EDIT_SHOT,
		promise: EditShot(token, shot, meetingSlug, channelId, useNewShotThumbs, skipThumbnailRegen),
	};
}

export const BULK_EDIT_SHOTS = 'BULK_EDIT_SHOTS';
export function bulkEditShots(token: string, shots: Shot[], deprecated: string = ''): AsyncAction {
	return {
		type: BULK_EDIT_SHOTS,
		promise: BulkEditShots(token, shots, deprecated),
	};
}

export const LOAD_SHOTS = 'LOAD_SHOTS';
export function loadShots(
	token: string,
	meeting: number,
): AsyncAction {
	return {
		type: LOAD_SHOTS,
		promise: LoadShots(token, meeting),
	};
}

export const LOAD_SHOT = 'LOAD_SHOT';
export function loadShot(token: string, meeting: number, shotId: number): AsyncAction {
	return {
		type: LOAD_SHOT,
		promise: LoadShot(token, meeting, shotId),
	};
}

export const SET_PLAYING_SHOT = 'SET_PLAYING_SHOT';
export function setPlayingShot(shotStartTimestamp: Entity | null): Action {
	return {
		type: SET_PLAYING_SHOT,
		payload: shotStartTimestamp,
	};
}

export const UPDATE_SHOT_ORDER = 'UPDATE_SHOT_ORDER';
export function updateShotOrder(shots: Shot[]): Action {
	return {
		type: UPDATE_SHOT_ORDER,
		payload: shots,
	};
}

export const SET_SHOT_MODAL_SHOT = 'SET_SHOT_MODAL_SHOT';
export function setShotModalShot(
	shotModalShot: Shot | null,
): Action {
	return {
		type: SET_SHOT_MODAL_SHOT,
		payload: shotModalShot,
	};
}

export const SET_SHOT_ID_TO_DELETE = 'SET_SHOT_ID_TO_DELETE';
export function setShotIdToDelete(shotIdToDelete: number | null): Action {
	return {
		type: SET_SHOT_ID_TO_DELETE,
		payload: shotIdToDelete,
	};
}

export const DELETE_SHOT = 'DELETE_SHOT';
export function deleteShot(token: string, shotId: number, meetingId: number): AsyncAction {
	return {
		type: DELETE_SHOT,
		promise: DeleteShot(token, shotId, meetingId),
	};
}

export const SYNC_SHOTS_REDUCER_STATE = 'SYNC_SHOTS_REDUCER_STATE';
export function syncShotsReducerState(state: any): Action {
	return {
		type: SYNC_SHOTS_REDUCER_STATE,
		payload: state,
	};
}

export const SET_CURRENT_SHOT_ID = 'SET_CURRENT_SHOT_ID';
export function setCurrentShotId(currentShot: number): Action {
	return {
		type: SET_CURRENT_SHOT_ID,
		payload: currentShot,
	};
}

export const SET_VIDEO_TITLES_HAVE_FIRED = 'SET_VIDEO_TITLES_HAVE_FIRED';
export function setVideoTitlesHaveFired(videoTitlesHaveFired: boolean): Action {
	return {
		type: SET_VIDEO_TITLES_HAVE_FIRED,
		payload: videoTitlesHaveFired,
	};
}

export const UPDATE_SHOT_DATA = 'UPDATE_SHOT_DATA';
export function updateShotData(shotId: number, shotData: ShotData, thumbnailUrl?: string, edited?: string): Action {
	return {
		type: UPDATE_SHOT_DATA,
		payload: { shotId, shotData, thumbnailUrl, edited },
	};
}

export const DISABLE_SHOT_AUTOPLAY = 'DISABLE_SHOT_AUTOPLAY';
export function disableShotAutoplay(shotId: number): Action {
	return {
		type: DISABLE_SHOT_AUTOPLAY,
		payload: shotId,
	};
}

export const UPDATE_CANVAS_STYLE = 'UPDATE_CANVAS_STYLE';
export function updateCanvasStyle(newStyle: AppSettings): Action {
	return {
		type: UPDATE_CANVAS_STYLE,
		payload: newStyle,
	};
}

export const REMOVE_SHOT = 'REMOVE_SHOT';
export function removeShot(shotId: number): Action {
	return {
		type: REMOVE_SHOT,
		payload: shotId,
	};
}

export const SET_DELETED_SHOT_ORDER = 'SET_DELETED_SHOT_ORDER';
export function setDeletedShotOrder(deletedShotOrder: number | null): Action {
	return {
		type: SET_DELETED_SHOT_ORDER,
		payload: deletedShotOrder,
	};
}

export const DUPLICATE_SHOT = 'DUPLICATE_SHOT';
export function duplicateShot(duplicatedShot: Shot): Action {
	return {
		type: DUPLICATE_SHOT,
		payload: duplicatedShot,
	};
}

export const SET_SHOT_TAB_SHOT_ID = 'SET_SHOT_TAB_SHOT_ID';
export function setShotTabShotId(shotTabShotId: number): Action {
	return {
		type: SET_SHOT_TAB_SHOT_ID,
		payload: shotTabShotId,
	};
}

export const SET_SHOT_MODAL_LOCKED = 'SET_SHOT_MODAL_LOCKED';
export function setShotModalLocked(editTimestamp: number): Action {
	return {
		type: SET_SHOT_MODAL_LOCKED,
		payload: editTimestamp,
	};
}

export const GENERATE_SHOT_THUMBNAIL = 'GENERATE_SHOT_THUMBNAIL';
export function generateShotThumbnail(
	token: string,
	meetingSlug: string,
	shotId: number,
	channelId: number,
	room?: number,
): AsyncAction {
	return {
		type: GENERATE_SHOT_THUMBNAIL,
		promise: GenerateShotThumbnail(token, meetingSlug, shotId, channelId, room),
	};
}

export const GENERATE_ALL_SHOT_THUMBNAILS = 'GENERATE_ALL_SHOT_THUMBNAILS';
export function generateAllShotThumbnails(
	token: string,
	meetingSlug: string,
	channelId: number,
	room: number,
	onlyMissing?: boolean,
): AsyncAction {
	return {
		type: GENERATE_ALL_SHOT_THUMBNAILS,
		promise: GenerateAllShotThumbnails(token, meetingSlug, channelId, room, onlyMissing),
	};
}

export const ADD_GENERATING_THUMBNAIL = 'ADD_GENERATING_THUMBNAIL';
export function addGeneratingThumbnail(shotId: number): Action {
	return { type: ADD_GENERATING_THUMBNAIL, payload: shotId };
}

export const REMOVE_GENERATING_THUMBNAIL = 'REMOVE_GENERATING_THUMBNAIL';
export function removeGeneratingThumbnail(shotId: number): Action {
	return { type: REMOVE_GENERATING_THUMBNAIL, payload: shotId };
}
